import { isMastheadLinkList } from 'utils/type-guards';

import { Banner, Market, MastheadData, MastheadLink, MastheadLinkList } from '../types';

import { getMarketIdFromMarket, getMarketFromMarketId } from './marketHelper';

type MarketList = string[] | Market[];

interface MarketItem {
  markets?: MarketList;
  availableMarkets?: MarketList;
}

export function isAvailableInMarkets<T extends MarketItem>(item: T, market: string): boolean {
  // Normalize market to sanity marketId
  const sanityMarketId = getMarketIdFromMarket(getMarketFromMarketId(market));
  const marketList = item.markets || item.availableMarkets || [];
  const availableMarketList = marketList.map(m => (typeof m === 'string' ? m : m._ref));
  return availableMarketList.includes(sanityMarketId);
}

export function filterByMarket<T extends MarketItem>(items: T[], market: string): T[] {
  return items.filter(item => isAvailableInMarkets(item, market));
}

function shouldFilterMastheadLink<T extends MastheadLinkList | MastheadLink>(item: T, market: string): boolean {
  return !item.isMarketSpecific || isAvailableInMarkets(item, market);
}

function filterMastheadLinks<T extends MastheadLinkList | MastheadLink>(items: T[] = [], market: string): T[] {
  return items
    .filter(item => shouldFilterMastheadLink(item, market))
    .map(item => {
      if (isMastheadLinkList(item)) {
        return {
          ...item,
          entries: item.entries.filter(entry => shouldFilterMastheadLink(entry.link, market)),
        };
      }
      return item;
    });
}

export const marketFilterMasthead = (market: string, mastheadData?: MastheadData) => {
  if (!mastheadData) {
    return null;
  }
  const { header, footer } = mastheadData;
  return {
    ...mastheadData,
    header: {
      ...header,
      primaryLinks: filterMastheadLinks(header.primaryLinks, market),
      secondaryLinks: filterMastheadLinks(header.secondaryLinks, market),
      profileLinks: filterMastheadLinks(header.profileLinks, market),
    },
    footer: {
      ...footer,
      columns: filterMastheadLinks(footer.columns, market),
      corporateLinks: filterMastheadLinks(footer.corporateLinks, market),
      social: filterMastheadLinks(footer.social, market),
    },
  };
};

export const marketFilterBanner = (market: string, banners?: Banner[]) => {
  if (!banners || !banners.length) {
    return null;
  }
  const filteredBanners = filterByMarket(banners, market);
  return filteredBanners[0] ?? null;
};
